import { confirmar } from '@/components/ui/confirm-dialog';
import {
  lineCost,
  lineMarginAmount,
  quoteMargin,
  roundAmount,
} from '@/lib/quote-metrics.generated';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AppLayout } from '@/components/layout/app-layout';
import { PageHeader } from '@/components/patterns';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Textarea } from '@/components/ui/textarea';
import {
  ArrowLeft,
  FilePlus,
  Loader2,
  Plus,
  LayoutTemplate,
  ArrowRight,
  Link2,
  Save,
  Ship,
  AlertTriangle,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/lib/hooks/use-toast';
import { useQuoteStore } from '../store/quote-store';
import { useMasterDataStore } from '@/features/master-data/store/master-data-store';
import { useModelStore } from '@/features/pricing-models/store/model-store';
import { useMaterialStore } from '@/features/materials/store/material-store';
import { UpstreamMaterialPrice } from '../types/quote.types';
import { quoteService } from '../services/quote-service';
import { QuoteSectionCard, SectionState } from '../components/quote-section-card';
import type { QuoteShipment } from '../types/quote-shipment.types';
import { formatNumber } from '@/lib/utils/format';
import { useCustomerStore } from '@/features/customers/store/customer-store';
import { QuickCreateCustomerDialog } from '@/features/customers/components/quick-create-customer-dialog';
import { importTrackingService } from '@/features/import-tracking/services/import-tracking-service';
import { QuoteLogisticsSection } from '../components/quote-logistics-section';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

let nextSectionId = 1;
function generateSectionId(): string {
  return `section-${nextSectionId++}`;
}

export function QuoteCreatePage() {
  const navigate = useNavigate();
  const { id: urlId } = useParams<{ id: string }>();
  const [autoSavedId, setAutoSavedId] = useState<string | null>(null);
  const id = urlId || autoSavedId || undefined;
  const { toast } = useToast();
  const {
    createQuote,
    updateQuote,
    saveDraft,
    updateDraft,
    fetchQuote,
    currentQuote,
    isLoading,
    quoteTypes,
    fetchQuoteTypes,
  } = useQuoteStore();
  const { currencies, lobs, countries, fetchCurrencies, fetchLOBs, fetchCountries } =
    useMasterDataStore();
  const { models, fetchModels, isLoading: modelsLoading } = useModelStore();
  const { materials, fetchMaterials } = useMaterialStore();
  const { customers, fetchCustomers } = useCustomerStore();

  const [searchParams] = useSearchParams();
  const sourceImportId = searchParams.get('sourceImportId');

  // isEditMode: true sólo si la URL tiene id (no incluye drafts auto-guardados in-place).
  const isEditMode = Boolean(urlId);

  const [formData, setFormData] = useState({
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    lobId: '',
    currencyId: '',
    countryId: '',
    c4cOpportunityId: '',
    c4cTaskId: '',
    validityDays: 30,
    notes: '',
  });

  const [showQuickCreateCustomer, setShowQuickCreateCustomer] = useState(false);

  const [quoteTypeId, setQuoteTypeId] = useState<string>('');
  const [upstreamPrices, setUpstreamPrices] = useState<Record<string, UpstreamMaterialPrice>>({});
  const [sourceQuoteId, setSourceQuoteId] = useState<string>('');
  const [approvedUpstreamQuotes, setApprovedUpstreamQuotes] = useState<
    Array<{
      id: string;
      code: string;
      clientName: string;
      updatedAt: string;
      lineItemCount: number;
    }>
  >([]);
  type SourceQuoteItem = {
    materialId: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
  };
  type SourceQuoteSection = {
    sectionId: string;
    sectionLabel: string;
    items: SourceQuoteItem[];
  };
  const [sourceQuoteSections, setSourceQuoteSections] = useState<SourceQuoteSection[]>([]);
  const [sourceQuoteAllItems, setSourceQuoteAllItems] = useState<SourceQuoteItem[]>([]);
  const [sourceQuoteCode, setSourceQuoteCode] = useState<string>('');

  const [sourceImportInfo, setSourceImportInfo] = useState<{
    orderNumber: string;
    supplierName: string;
    incoterm: string;
    totalLandedCost: number;
    currency: string;
  } | null>(null);

  const [quoteIncoterm, setQuoteIncoterm] = useState<string | null>(null);
  const [incotermConfig] = useState<any>(null);
  const [freightTotals, setFreightTotals] = useState({ totalFreight: 0, totalInsurance: 0 });
  const [localFreightLegs, setLocalFreightLegs] = useState<any[]>([]);
  const [logisticsRefreshKey, setLogisticsRefreshKey] = useState(0);
  const [shipments, setShipments] = useState<QuoteShipment[]>([]);

  const selectedQuoteType = quoteTypes?.find((qt) => qt.id === quoteTypeId);

  const [sections, setSections] = useState<SectionState[]>([
    {
      id: generateSectionId(),
      modelId: '',
      order: 0,
      globalInputs: {},
      variableLinks: {},
      lineItems: [],
      variables: [],
    },
  ]);

  // Mapa de flete por línea — siempre fresquito con datos de shipments + sections.
  const freightByLineItemId = useMemo(() => {
    const map: Record<string, number> = {};
    const allLocal = sections.flatMap((s) => s.lineItems);

    // 1. Backend prorateo (líneas persistidas con proratedFreightCost)
    for (const s of shipments) {
      for (const li of s.lineItems ?? []) {
        if (li.id && li.proratedFreightCost != null && li.proratedFreightCost > 0) {
          map[li.id] = li.proratedFreightCost;
        }
      }
    }

    // 2. Local prorateo por costo total (líneas temp asignadas via checkbox)
    for (const ship of shipments) {
      if (!ship.totalCost || ship.totalCost <= 0) continue;
      const localAssigned = allLocal.filter((li) => li.shipmentId === ship.id && !map[li.id]);
      if (localAssigned.length === 0) continue;
      const totalCost = localAssigned.reduce((sum, li) => sum + (Number(li.totalPrice) || 0), 0);
      for (const li of localAssigned) {
        const liCost = Number(li.totalPrice) || 0;
        map[li.id] =
          totalCost > 0
            ? ship.totalCost * (liCost / totalCost)
            : ship.totalCost / localAssigned.length;
      }
    }

    return map;
  }, [shipments, sections]);

  // Load master data on mount
  useEffect(() => {
    fetchCurrencies();
    fetchLOBs();
    fetchCountries();
    fetchModels();
    fetchMaterials();
    fetchCustomers();
    fetchQuoteTypes();
  }, []);

  // Pre-populate from import quotation
  useEffect(() => {
    if (!sourceImportId || isEditMode) return;

    const loadImportData = async () => {
      try {
        const [order, items] = await Promise.all([
          importTrackingService.getOrder(sourceImportId),
          importTrackingService.getOrderItems(sourceImportId),
        ]);

        setSourceImportInfo({
          orderNumber: order.orderNumber,
          supplierName: order.supplier?.name || '',
          incoterm: order.incoterm,
          totalLandedCost: order.totalLandedCost,
          currency: order.currency,
        });

        // Pre-populate line items from import items that have materialId and landed cost
        const importLineItems = items
          .filter((item: any) => item.materialId && item.unitLandedCost)
          .map((item: any, idx: number) => ({
            id: `import-${idx}`,
            materialId: item.materialId,
            quantity: item.quantity,
            costInputs: {} as Record<string, number>,
            unitPrice: item.unitLandedCost || 0,
            totalPrice: (item.unitLandedCost || 0) * item.quantity,
            calculations: {},
          }));

        if (importLineItems.length > 0) {
          setSections((prev) => [
            {
              ...prev[0],
              lineItems: importLineItems,
            },
          ]);
        }

        // Pre-populate incoterm from import
        if (order.incoterm) {
          setQuoteIncoterm(order.incoterm);
        }

        // Set notes referencing the import
        setFormData((prev) => ({
          ...prev,
          notes: `Basada en cotización de importación ${order.orderNumber} (${order.incoterm}) - Proveedor: ${order.supplier?.name || ''}`,
        }));
      } catch (err) {
        console.error('Error loading import data:', err);
      }
    };

    loadImportData();
  }, [sourceImportId, isEditMode]);

  // Load existing quote if editing
  useEffect(() => {
    if (isEditMode && id) {
      fetchQuote(id);
    }
  }, [id, isEditMode]);

  // Pre-populate form when quote loads (edit mode)
  useEffect(() => {
    if (isEditMode && currentQuote) {
      setFormData({
        clientName: currentQuote.clientName,
        clientEmail: currentQuote.clientEmail,
        clientPhone: currentQuote.clientPhone || '',
        lobId: currentQuote.lobId || '',
        currencyId: currentQuote.currencyId,
        countryId: currentQuote.countryId || '',
        c4cOpportunityId: currentQuote.c4cOpportunityId || '',
        c4cTaskId: currentQuote.c4cTaskId || '',
        validityDays: currentQuote.validityDays || 30,
        notes: currentQuote.notes || '',
      });

      // Restore sections
      if (currentQuote.sections && currentQuote.sections.length > 0) {
        setSections(
          currentQuote.sections.map((s, idx) => ({
            id: s.id || generateSectionId(),
            modelId: s.modelId,
            order: s.order ?? idx,
            globalInputs: s.globalInputs || {},
            variableLinks: (s.variableLinks as Record<string, any>) || {},
            lineItems: (s.lineItems || []).map((li, liIdx) => ({
              id: li.id || `loaded-${idx}-${liIdx}`,
              materialId: li.materialId,
              quantity: li.quantity,
              costInputs: li.costInputs || {},
              unitPrice: li.unitPrice,
              totalPrice: li.totalPrice,
              calculations: li.calculations || {},
              shipmentId: li.shipmentId ?? null,
            })),
            variables: s.model?.variables || [],
          }))
        );
      } else if (currentQuote.modelId) {
        // Legacy single-model quote
        setSections([
          {
            id: generateSectionId(),
            modelId: currentQuote.modelId,
            order: 0,
            globalInputs: currentQuote.globalInputs || {},
            variableLinks: {},
            lineItems: (currentQuote.lineItems || []).map((li, idx) => ({
              id: li.id || `loaded-${idx}`,
              materialId: li.materialId,
              quantity: li.quantity,
              costInputs: li.costInputs || {},
              unitPrice: li.unitPrice,
              totalPrice: li.totalPrice,
              calculations: li.calculations || {},
              shipmentId: li.shipmentId ?? null,
            })),
            variables: currentQuote.model?.variables || [],
          },
        ]);
      }
    }
  }, [currentQuote, isEditMode]);

  // Restore quoteTypeId and incoterm in edit mode
  useEffect(() => {
    if (isEditMode && currentQuote?.quoteTypeId) {
      setQuoteTypeId(currentQuote.quoteTypeId);
    }
    if (isEditMode && (currentQuote as any)?.incoterm) {
      setQuoteIncoterm((currentQuote as any).incoterm);
    }
  }, [currentQuote, isEditMode]);

  // Fetch approved upstream quotes when a downstream type is selected
  useEffect(() => {
    if (!selectedQuoteType?.upstreamTypeId) {
      setApprovedUpstreamQuotes([]);
      setSourceQuoteId('');
      return;
    }
    quoteService
      .getApprovedUpstreamQuotes(selectedQuoteType.upstreamTypeId)
      .then(setApprovedUpstreamQuotes)
      .catch(console.error);
  }, [selectedQuoteType?.upstreamTypeId]);

  // Fetch line items of the selected source quote (agrupados por sección)
  useEffect(() => {
    if (!sourceQuoteId) {
      setSourceQuoteSections([]);
      setSourceQuoteAllItems([]);
      setSourceQuoteCode('');
      return;
    }
    quoteService
      .getSourceQuoteLineItems(sourceQuoteId)
      .then((res) => {
        const allSections = [...(res?.sections ?? [])];
        if ((res?.legacyItems?.length ?? 0) > 0) {
          allSections.push({
            sectionId: '__legacy__',
            sectionLabel: 'Items sin sección (legacy)',
            items: res.legacyItems,
          });
        }
        setSourceQuoteSections(allSections);
        setSourceQuoteAllItems(res?.allItems ?? []);
        setSourceQuoteCode(res?.sourceQuoteCode ?? '');
      })
      .catch((err) => {
        console.error('Error cargando items de cotización origen:', err);
        setSourceQuoteSections([]);
        setSourceQuoteAllItems([]);
        setSourceQuoteCode('');
      });
  }, [sourceQuoteId]);

  // Resolve upstream prices when materials change and we have a downstream type
  useEffect(() => {
    if (!quoteTypeId || !selectedQuoteType?.upstreamTypeId) return;

    const allMaterialIds = [
      ...new Set(sections.flatMap((s) => s.lineItems.map((li) => li.materialId).filter(Boolean))),
    ];
    if (allMaterialIds.length === 0) return;

    const targetCurrency = currencies.find((c) => c.id === formData.currencyId)?.code;
    const referenceDate = new Date().toISOString();

    quoteService
      .resolveUpstreamPrices(
        quoteTypeId,
        allMaterialIds,
        sourceQuoteId || undefined,
        targetCurrency,
        referenceDate
      )
      .then((prices) => {
        setUpstreamPrices(prices);

        // Detectar errores de FX: si algún material viene con error no_exchange_rate,
        // avisamos al usuario (bloqueo no destructivo — la cotización no se puebla con ese material).
        const fxErrors = Object.values(prices).filter((p) => p?.error === 'no_exchange_rate');
        if (fxErrors.length > 0) {
          const first = fxErrors[0];
          toast({
            title: 'Falta tipo de cambio',
            description: `No hay TC ${first.sourceCurrency}→${first.currency} registrado para la fecha de la cotización. Cargalo en Datos Maestros > Tipos de Cambio antes de continuar.`,
            variant: 'destructive',
          });
        } else {
          // Warning suave si el rate aplicado es viejo (>7 días).
          const stale = Object.values(prices).find((p) => p?.available && p.conversion?.stale);
          if (stale?.conversion) {
            toast({
              title: 'Tipo de cambio antiguo',
              description: `Se aplicó el TC ${stale.conversion.fromCurrency}→${stale.conversion.toCurrency} del ${stale.conversion.effectiveDate} (${stale.conversion.ageDays} días de antigüedad). Considerá registrar uno actualizado.`,
            });
          }
        }

        // Auto-inject upstream prices into line item costInputs
        if (selectedQuoteType?.upstreamVariableCode) {
          const varCode = selectedQuoteType.upstreamVariableCode;
          setSections((prev) =>
            prev.map((section) => ({
              ...section,
              lineItems: section.lineItems.map((li) => {
                const up = prices[li.materialId];
                if (up?.available && li.costInputs[varCode] !== up.unitPrice) {
                  return { ...li, costInputs: { ...li.costInputs, [varCode]: up.unitPrice } };
                }
                return li;
              }),
            }))
          );
        }
      })
      .catch(() => {
        toast({
          title: 'Error',
          description:
            'No se pudieron cargar los precios upstream. Verifique los valores manualmente.',
          variant: 'destructive',
        });
      });
  }, [
    quoteTypeId,
    selectedQuoteType,
    sourceQuoteId,
    formData.currencyId,
    sections.flatMap((s) => s.lineItems.map((li) => li.materialId)).join(','),
  ]);

  const selectedCurrency = currencies.find((c) => c.id === formData.currencyId);

  // Grand total from all sections (selling price)
  const grandTotal = roundAmount(
    sections.reduce((sum, s) => sum + s.lineItems.reduce((lSum, li) => lSum + li.totalPrice, 0), 0)
  );

  // === MARGEN REAL basado en roles del modelo (priceRole) ===
  // El modelo declara explícitamente qué variable cumple cada rol:
  //   COST_BASE_MARGIN → base de costo sobre la que se aplica el margen
  //   COST_INPUT       → fallback si no hay COST_BASE_MARGIN
  //   MARGIN           → monto de margen puro (sin impuestos/previsiones)
  //
  // Las fallbacks por nombre de variable quedan por retrocompat con modelos viejos.
  const computeLineItemCost = (li: any, sectionVars: any[]): number =>
    lineCost(li, sectionVars) ?? 0;
  const computeLineItemMarginAmount = lineMarginAmount;

  const grandBaseCost = roundAmount(
    sections.reduce(
      (sum, s) =>
        sum +
        s.lineItems.reduce((lSum, li) => lSum + computeLineItemCost(li, s.variables || []), 0),
      0
    )
  );

  const grandMarginAmount: number | null = (() => {
    let total = 0;
    let anyFound = false;
    for (const s of sections) {
      for (const li of s.lineItems) {
        const m = computeLineItemMarginAmount(li, s.variables || []);
        if (m !== null) {
          anyFound = true;
          total += m;
        }
      }
    }
    return anyFound ? roundAmount(total) : null;
  })();

  // actualMargin = margen del vendedor / baseCost × 100.
  // Si ninguna línea expone MARGIN, fallback: (final − base) / final.
  const computeActualMarginPct = (finalPrice: number): number => {
    return quoteMargin(grandBaseCost, grandMarginAmount, finalPrice).percentage ?? 0;
  };

  const handleAddSection = () => {
    setSections([
      ...sections,
      {
        id: generateSectionId(),
        modelId: '',
        order: sections.length,
        globalInputs: {},
        variableLinks: {},
        lineItems: [],
        variables: [],
      },
    ]);
  };

  // Sync linked variables across sections: when a section's lineItems change,
  // auto-update globalInputs in other sections that link to those items.
  const syncLinkedVariables = useCallback((updatedSections: SectionState[]): SectionState[] => {
    return updatedSections.map((section) => {
      if (Object.keys(section.variableLinks).length === 0) return section;

      const newGlobalInputs = { ...section.globalInputs };
      let changed = false;

      Object.entries(section.variableLinks).forEach(([varCode, link]) => {
        const sourceSection = updatedSections.find((s) => s.id === link.sourceSectionId);
        const sourceItem = sourceSection?.lineItems.find((li) => li.id === link.sourceItemId);
        if (sourceItem && newGlobalInputs[varCode] !== sourceItem.totalPrice) {
          newGlobalInputs[varCode] = sourceItem.totalPrice;
          changed = true;
        }
      });

      return changed ? { ...section, globalInputs: newGlobalInputs } : section;
    });
  }, []);

  const handleUpdateSection = useCallback(
    (index: number, updated: SectionState) => {
      setSections((prev) => {
        const next = prev.map((s, i) => (i === index ? updated : s));
        return syncLinkedVariables(next);
      });
    },
    [syncLinkedVariables]
  );

  const handleRemoveSection = (index: number) => {
    if (sections.length <= 1) return;
    setSections(sections.filter((_, i) => i !== index));
  };

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSaveDraft = async (options?: {
    redirectTo?: 'detail' | 'edit' | 'none';
    silent?: boolean;
  }) => {
    const silent = !!options?.silent;
    // Solo requerimos nombre de cliente para borrador
    if (!formData.clientName.trim()) {
      setFormErrors({ clientName: 'El cliente es obligatorio' });
      if (!silent) {
        toast({
          title: 'Datos incompletos',
          description: 'Ingresa al menos el nombre del cliente para guardar el borrador',
          variant: 'destructive',
        });
        document
          .querySelector('[data-tour="create-client"]')
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    setFormErrors({});

    try {
      setIsSavingDraft(true);
      const draftData: any = {
        ...formData,
        incoterm: quoteIncoterm,
        quoteTypeId: quoteTypeId || null,
        sourceQuoteId: sourceQuoteId || null,
        modelId: sections.length === 1 ? sections[0].modelId : null,
        costInputs: sections[0]?.globalInputs || {},
        globalInputs: sections[0]?.globalInputs || {},
        baseCost: grandBaseCost,
        targetMargin: 0,
        suggestedPrice: grandTotal,
        finalPrice: grandTotal,
        actualMargin: computeActualMarginPct(grandTotal),
        taxes: [],
        currencyId: formData.currencyId || null,
        lobId: formData.lobId || null,
        countryId: formData.countryId || null,
        validityDays: formData.validityDays,
        validUntil: new Date(
          Date.now() + formData.validityDays * 24 * 60 * 60 * 1000
        ).toISOString(),
        sections: sections
          .filter((s) => s.modelId)
          .map((s, idx) => ({
            modelId: s.modelId,
            order: idx,
            globalInputs: s.globalInputs,
            variableLinks: Object.keys(s.variableLinks).length > 0 ? s.variableLinks : undefined,
            lineItems: s.lineItems
              .filter((li) => li.materialId)
              .map((li, liIdx) => ({
                materialId: li.materialId,
                lineNumber: liIdx,
                quantity: li.quantity,
                costInputs: li.costInputs,
                calculations: li.calculations,
                unitPrice: li.unitPrice,
                totalPrice: li.totalPrice,
                shipmentId: li.shipmentId || null,
              })),
          })),
        paymentScenarios: [],
        c4cOpportunityId: formData.c4cOpportunityId || null,
        c4cTaskId: formData.c4cTaskId || null,
        freightLegs:
          !id && localFreightLegs.length > 0
            ? localFreightLegs.map((leg) => ({
                legOrder: leg.legOrder,
                origin: leg.origin,
                destination: leg.destination,
                transportMode: leg.transportMode,
                legType: leg.legType,
                chargeMethod: leg.chargeMethod,
                ratePerCbm: leg.ratePerCbm,
                ratePerKg: leg.ratePerKg,
                flatRate: leg.flatRate,
                manualAmount: leg.manualAmount,
                minimumCharge: leg.minimumCharge,
                hasInsurance: leg.hasInsurance,
                insuranceRate: leg.insuranceRate,
                estimatedDays: leg.estimatedDays,
                surcharges:
                  leg.surcharges?.map((s: any) => ({
                    concept: s.concept,
                    type: s.type,
                    value: s.value,
                  })) || [],
              }))
            : undefined,
      };

      let quote;
      if (id) {
        quote = await updateDraft(id, draftData);
        if (!silent) {
          toast({
            title: 'Borrador actualizado',
            description: 'Los cambios se guardaron como borrador',
            variant: 'success',
          });
        }
      } else {
        quote = await saveDraft(draftData);
        if (!silent) {
          toast({
            title: 'Borrador guardado',
            description: 'La cotización se guardó como borrador',
            variant: 'success',
          });
        }
      }

      const redirectTo = options?.redirectTo ?? 'detail';
      if (redirectTo === 'edit') {
        navigate(`/quotes/${quote.id}/edit`, { replace: true });
      } else if (redirectTo === 'detail') {
        navigate(`/quotes/${quote.id}`);
      }
      return quote.id as string;
    } catch (error: any) {
      console.error('[Quote Draft] Error:', error.response?.data || error.message);
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Error al guardar borrador';
      if (!silent) {
        toast({ title: 'Error', description: errorMessage, variant: 'destructive' });
      }
    } finally {
      setIsSavingDraft(false);
    }
  };

  // ---- Shipment handlers (feature: envío por línea) ----

  /**
   * Garantiza que el quote esté guardado (devuelve su id).
   * Si no estamos en modo edición y no hay id aún, crea el borrador en silencio.
   * Dedupe con inFlightSaveRef para evitar doble-save si el hijo dispara el autoguardado
   * en paralelo con un click manual.
   */
  const inFlightSaveRef = useRef<Promise<string | null> | null>(null);
  const handleSaveDraftRef = useRef(handleSaveDraft);
  handleSaveDraftRef.current = handleSaveDraft;
  const idRef = useRef(id);
  idRef.current = id;
  const ensureSaved = useCallback(async (): Promise<string | null> => {
    if (idRef.current) return idRef.current;
    if (inFlightSaveRef.current) return inFlightSaveRef.current;
    const p = (async () => {
      try {
        const newId = await handleSaveDraftRef.current({ redirectTo: 'none', silent: true });
        if (newId) setAutoSavedId(newId);
        return newId ?? null;
      } catch {
        return null;
      }
    })();
    inFlightSaveRef.current = p;
    try {
      return await p;
    } finally {
      inFlightSaveRef.current = null;
    }
  }, []);

  // Shipment handlers — 100% estado local. Se persisten al guardar el borrador.
  const handleShipmentChange = useCallback((lineItemId: string, shipmentId: string) => {
    setSections((prev) =>
      prev.map((s) => ({
        ...s,
        lineItems: s.lineItems.map((li) =>
          li.id === lineItemId ? { ...li, shipmentId: shipmentId || null } : li
        ),
      }))
    );
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Lock contra doble-click: si ya estamos enviando, ignoramos clicks siguientes.
    if (isSubmitting) return;

    // Validate basic info
    const basicErrors: Record<string, string> = {};
    if (!formData.clientName.trim()) basicErrors.clientName = 'El cliente es obligatorio';
    if (!formData.clientEmail.trim()) basicErrors.clientEmail = 'El email es obligatorio';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.clientEmail))
      basicErrors.clientEmail = 'Email inválido';
    if (!formData.currencyId) basicErrors.currencyId = 'La divisa es obligatoria';

    if (Object.keys(basicErrors).length > 0) {
      setFormErrors(basicErrors);
      toast({
        title: 'Datos incompletos',
        description: 'Completa los campos obligatorios de información básica',
        variant: 'destructive',
      });
      document
        .querySelector('[data-tour="create-client"]')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setFormErrors({});

    // Validate at least one section has items
    const allLineItems = sections.flatMap((s) => s.lineItems);
    if (allLineItems.length === 0) {
      toast({
        title: 'Error',
        description: 'Agrega al menos un item a la cotización',
        variant: 'destructive',
      });
      return;
    }

    const hasEmptyMaterial = allLineItems.some((li) => !li.materialId);
    if (hasEmptyMaterial) {
      toast({
        title: 'Error',
        description: 'Todos los items deben tener un material seleccionado',
        variant: 'destructive',
      });
      return;
    }

    const hasEmptyModel = sections.some((s) => !s.modelId);
    if (hasEmptyModel) {
      toast({
        title: 'Error',
        description: 'Todas las secciones deben tener un modelo seleccionado',
        variant: 'destructive',
      });
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError(null);
      // Si un autoguardado está en curso, esperamos para evitar crear dos borradores.
      if (!id && inFlightSaveRef.current) {
        await inFlightSaveRef.current;
      }
      // Releer id después del await (pudo actualizarse por autosave).
      const effectiveId = idRef.current;

      // Total = suma de Total Línea de todas las secciones + fletes
      const totalFromVariables =
        grandTotal + freightTotals.totalFreight + freightTotals.totalInsurance;

      const quoteData: any = {
        ...formData,
        incoterm: quoteIncoterm,
        quoteTypeId: quoteTypeId || null,
        sourceQuoteId: sourceQuoteId || null,
        modelId: sections.length === 1 ? sections[0].modelId : null,
        costInputs: sections[0]?.globalInputs || {},
        globalInputs: sections[0]?.globalInputs || {},
        baseCost: grandBaseCost,
        targetMargin: 0,
        suggestedPrice: totalFromVariables,
        finalPrice: totalFromVariables,
        actualMargin: computeActualMarginPct(totalFromVariables),
        taxes: [],
        sections: sections.map((s, idx) => ({
          modelId: s.modelId,
          order: idx,
          globalInputs: s.globalInputs,
          variableLinks: Object.keys(s.variableLinks).length > 0 ? s.variableLinks : undefined,
          lineItems: s.lineItems.map((li, liIdx) => ({
            materialId: li.materialId,
            lineNumber: liIdx,
            quantity: li.quantity,
            costInputs: li.costInputs,
            calculations: li.calculations,
            unitPrice: li.unitPrice,
            totalPrice: li.totalPrice,
            shipmentId: li.shipmentId || null,
          })),
        })),
        paymentScenarios: [],
        c4cOpportunityId: formData.c4cOpportunityId || null,
        c4cTaskId: formData.c4cTaskId || null,
        lobId: formData.lobId || null,
        countryId: formData.countryId || null,
        validityDays: formData.validityDays,
        validUntil: new Date(
          Date.now() + formData.validityDays * 24 * 60 * 60 * 1000
        ).toISOString(),
      };

      let quote;
      if (effectiveId) {
        quote = await updateQuote(effectiveId, quoteData);
        toast({
          title: 'Cotización actualizada',
          description: 'Los cambios se guardaron correctamente',
          variant: 'success',
        });
      } else {
        quote = await createQuote(quoteData);
        toast({
          title: 'Cotización creada',
          description: 'La cotización se creó exitosamente',
          variant: 'success',
        });
      }

      // Limpiar localStorage del borrador (defensa en profundidad).
      try {
        window.localStorage.removeItem('quote-draft');
        window.localStorage.removeItem('quote-create-draft');
        window.localStorage.removeItem('quote-store');
      } catch {
        // ignore
      }
      window.location.href = `/quotes/${quote.id}`;
      return;
    } catch (error: any) {
      console.error('[Quote Create] Error:', error.response?.data || error.message);
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Error al crear cotización';
      setSubmitError(errorMessage);
      toast({ title: 'Error', description: errorMessage, variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ---- Reset del borrador local ----
  // Limpia state in-memory y cualquier persistencia (defensa en profundidad: hoy
  // el store no usa Zustand persist, pero limpiamos cualquier key conocida por
  // las dudas).
  const resetDraft = useCallback(() => {
    try {
      window.localStorage.removeItem('quote-draft');
      window.localStorage.removeItem('quote-create-draft');
      window.localStorage.removeItem('quote-store');
    } catch {
      // ignore
    }
    setFormData({
      clientName: '',
      clientEmail: '',
      clientPhone: '',
      lobId: '',
      currencyId: '',
      countryId: '',
      c4cOpportunityId: '',
      c4cTaskId: '',
      validityDays: 30,
      notes: '',
    });
    setQuoteTypeId('');
    setSourceQuoteId('');
    setSections([
      {
        id: generateSectionId(),
        modelId: '',
        order: 0,
        globalInputs: {},
        variableLinks: {},
        lineItems: [],
        variables: [],
      },
    ]);
    setUpstreamPrices({});
    setQuoteIncoterm(null);
    setFreightTotals({ totalFreight: 0, totalInsurance: 0 });
    setLocalFreightLegs([]);
    setShipments([]);
    setLogisticsRefreshKey((k) => k + 1);
    setFormErrors({});
    setSubmitError(null);
    setAutoSavedId(null);
  }, []);

  // Detectar si hay datos del usuario en el form (para mostrar "Empezar de cero").
  const hasDraftData =
    !isEditMode &&
    (!!formData.clientName.trim() ||
      !!formData.clientEmail.trim() ||
      !!formData.clientPhone.trim() ||
      !!formData.notes.trim() ||
      !!quoteTypeId ||
      !!autoSavedId ||
      sections.some((s) => !!s.modelId || s.lineItems.length > 0));

  return (
    <AppLayout>
      <div className="mx-auto max-w-[1440px] px-4 py-8 md:px-8">
        <div className="max-w-7xl space-y-6">
          {/* El encabezado de Cotiza (artefacto «Nueva cotización», 19-oct). */}
          <PageHeader
            volver={{ texto: 'Cotizaciones', onClick: () => navigate('/quotes') }}
            modulo="cotiza"
            etiqueta="Cotiza"
            icono={FilePlus}
            titulo={isEditMode ? 'Editar cotización' : 'Nueva cotización'}
            subtitulo={
              isEditMode
                ? 'Cambiá los datos de la cotización.'
                : 'Completá los datos y el sistema calcula el precio con el modelo elegido.'
            }
          />

          {/* Banner: cotización en aprobación */}
          {isEditMode && currentQuote?.status === 'PENDING_APPROVAL' && (
            <div className="rounded-lg border border-alerta-borde bg-alerta-suave p-4 flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-alerta-suave rounded-lg shrink-0">
                  <AlertTriangle className="w-5 h-5 text-alerta" />
                </div>
                <div>
                  <p className="font-semibold text-sm text-foreground">
                    Esta cotización está en aprobación
                  </p>
                  <p className="text-xs text-alerta mt-1 max-w-xl">
                    Si la editás y guardás, el flujo de aprobación se recalculará desde cero (los
                    aprobadores deberán revisarla nuevamente). Si no querés afectarla, volvé al
                    detalle.
                  </p>
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => navigate(`/quotes/${id}`)}
                className="shrink-0 border-alerta-borde text-foreground hover:bg-alerta-suave"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver al detalle
              </Button>
            </div>
          )}

          {/* Source import banner */}
          {sourceImportInfo && (
            <div className="rounded-lg border border-info-borde bg-info-suave p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-info-suave rounded-lg">
                  <Ship className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium text-sm">
                    Basada en cotización de importación {sourceImportInfo.orderNumber}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {sourceImportInfo.supplierName} - {sourceImportInfo.incoterm} - Landed Cost:{' '}
                    {new Intl.NumberFormat('es-BO', {
                      style: 'currency',
                      currency: sourceImportInfo.currency,
                    }).format(sourceImportInfo.totalLandedCost)}
                  </p>
                </div>
              </div>
              <Badge variant="secondary">Importación</Badge>
            </div>
          )}

          {/* Empty state: no models yet (create mode only) */}
          {!isEditMode && !modelsLoading && models.length === 0 ? (
            <div className="rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50 to-white p-8 text-center space-y-4">
              <div className="w-14 h-14 rounded-xl bg-violet-100 flex items-center justify-center mx-auto">
                <LayoutTemplate className="w-7 h-7 text-violet-600" />
              </div>
              <div>
                <h3 className="font-semibold text-base">No hay modelos de precios</h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
                  Para crear cotizaciones primero necesitas configurar al menos un modelo de precios
                  con sus variables y fórmulas.
                </p>
              </div>
              <Button onClick={() => navigate('/pricing-models')} className="gap-2">
                <LayoutTemplate className="w-4 h-4" />
                Crear modelo de precios
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA') {
                  e.preventDefault();
                }
              }}
            >
              <div className="space-y-6">
                {/* Quote Type Selector */}
                {quoteTypes?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Link2 className="w-5 h-5" />
                        Tipo de Cotización
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Tipo</Label>
                          <SearchableSelect
                            options={quoteTypes.map((qt) => ({
                              value: qt.id,
                              label: `${qt.codePrefix} — ${qt.name}`,
                            }))}
                            value={quoteTypeId || undefined}
                            onValueChange={(value) => {
                              setQuoteTypeId(value);
                              setUpstreamPrices({});
                              setSourceQuoteId('');
                            }}
                            placeholder="Selecciona tipo (opcional)"
                            searchPlaceholder="Buscar tipo..."
                          />
                        </div>
                        {selectedQuoteType && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Badge variant="outline">{selectedQuoteType.codePrefix}</Badge>
                            {selectedQuoteType.description && (
                              <span>{selectedQuoteType.description}</span>
                            )}
                            {selectedQuoteType.upstreamType && (
                              <Badge variant="secondary" className="ml-2">
                                Upstream: {selectedQuoteType.upstreamType.name}
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                      {selectedQuoteType?.upstreamTypeId && approvedUpstreamQuotes.length > 0 && (
                        <div className="mt-4 space-y-2">
                          <Label>Cotización de Origen</Label>
                          <SearchableSelect
                            options={approvedUpstreamQuotes.map((q) => ({
                              value: q.id,
                              label: `${q.code} — ${q.clientName || 'Sin cliente'} (${q.lineItemCount} items)`,
                            }))}
                            value={sourceQuoteId || undefined}
                            onValueChange={(value) => {
                              setSourceQuoteId(value);
                              setUpstreamPrices({});
                            }}
                            placeholder="Más reciente (por defecto)"
                            searchPlaceholder="Buscar cotización aprobada..."
                          />
                          <p className="text-xs text-muted-foreground">
                            Selecciona de cuál cotización de{' '}
                            {selectedQuoteType.upstreamType?.name || 'upstream'} tomar los costos.
                            Si no seleccionas ninguna, se usan los últimos precios aprobados.
                          </p>
                        </div>
                      )}
                      {selectedQuoteType?.upstreamVariableCode && (
                        <div className="mt-3 text-xs text-muted-foreground bg-muted/50 rounded-md p-2">
                          Los precios aprobados del tipo &quot;
                          {selectedQuoteType.upstreamType?.name || 'upstream'}&quot; se inyectarán
                          automáticamente en la variable{' '}
                          <code className="font-mono bg-muted px-1 rounded">
                            {selectedQuoteType.upstreamVariableCode}
                          </code>
                          {sourceQuoteId && (
                            <>
                              {' '}
                              desde{' '}
                              <strong>
                                {approvedUpstreamQuotes.find((q) => q.id === sourceQuoteId)?.code}
                              </strong>
                            </>
                          )}
                          .
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Basic Info */}
                <Card data-tour="create-client">
                  <CardHeader>
                    <CardTitle>Información Básica</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="clientName">Cliente *</Label>
                        <SearchableSelect
                          options={customers
                            .filter((c) => c.isActive)
                            .map((c) => ({ value: c.id, label: c.name }))}
                          value={customers.find((c) => c.name === formData.clientName)?.id}
                          onValueChange={(customerId) => {
                            const customer = customers.find((c) => c.id === customerId);
                            if (customer) {
                              setFormData({
                                ...formData,
                                clientName: customer.name,
                                clientEmail: customer.email || formData.clientEmail,
                                clientPhone: customer.phone || formData.clientPhone,
                              });
                              setFormErrors((prev) => {
                                const n = { ...prev };
                                delete n.clientName;
                                return n;
                              });
                            }
                          }}
                          placeholder="Selecciona un cliente"
                          searchPlaceholder="Buscar cliente..."
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="mt-1 h-7 text-xs text-muted-foreground hover:text-primary"
                          onClick={() => setShowQuickCreateCustomer(true)}
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Nuevo Cliente
                        </Button>
                        {formErrors.clientName && (
                          <p className="text-xs text-destructive">{formErrors.clientName}</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="clientEmail">Email del Cliente *</Label>
                        <Input
                          id="clientEmail"
                          type="email"
                          required
                          value={formData.clientEmail}
                          onChange={(e) => {
                            setFormData({ ...formData, clientEmail: e.target.value });
                            setFormErrors((prev) => {
                              const n = { ...prev };
                              delete n.clientEmail;
                              return n;
                            });
                          }}
                          error={formErrors.clientEmail}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="clientPhone">Teléfono</Label>
                        <Input
                          id="clientPhone"
                          value={formData.clientPhone}
                          onChange={(e) =>
                            setFormData({ ...formData, clientPhone: e.target.value })
                          }
                        />
                      </div>
                    </div>

                    <div
                      data-tour="create-details"
                      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4"
                    >
                      <div className="space-y-2">
                        <Label htmlFor="countryId">País</Label>
                        <SearchableSelect
                          options={countries
                            .filter((c) => c.isActive)
                            .map((c) => ({ value: c.id, label: `${c.code} - ${c.name}` }))}
                          value={formData.countryId || undefined}
                          onValueChange={(value) => setFormData({ ...formData, countryId: value })}
                          placeholder="Selecciona país"
                          searchPlaceholder="Buscar país..."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lobId">Line of Business</Label>
                        <SearchableSelect
                          options={lobs
                            .filter((l) => l.isActive)
                            .map((l) => ({ value: l.id, label: l.name }))}
                          value={formData.lobId || undefined}
                          onValueChange={(value) => setFormData({ ...formData, lobId: value })}
                          placeholder="Selecciona LOB"
                          searchPlaceholder="Buscar LOB..."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="currencyId">Divisa *</Label>
                        <SearchableSelect
                          options={currencies
                            .filter((c) => c.isActive)
                            .map((c) => ({ value: c.id, label: `${c.code} - ${c.name}` }))}
                          value={formData.currencyId || undefined}
                          onValueChange={(value) => {
                            setFormData({ ...formData, currencyId: value });
                            setFormErrors((prev) => {
                              const n = { ...prev };
                              delete n.currencyId;
                              return n;
                            });
                          }}
                          placeholder="Selecciona divisa"
                          searchPlaceholder="Buscar divisa..."
                        />
                        {formErrors.currencyId && (
                          <p className="text-xs text-destructive">{formErrors.currencyId}</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="validityDays">Validez (días) *</Label>
                        <Input
                          id="validityDays"
                          type="number"
                          min="1"
                          required
                          value={formData.validityDays}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              validityDays: parseInt(e.target.value) || 30,
                            })
                          }
                        />
                        <p className="text-xs text-muted-foreground">
                          Válida hasta:{' '}
                          {new Date(
                            Date.now() + formData.validityDays * 24 * 60 * 60 * 1000
                          ).toLocaleDateString('es-ES')}
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label>Incoterm</Label>
                        <Select value={quoteIncoterm || ''} onValueChange={setQuoteIncoterm}>
                          <SelectTrigger>
                            <SelectValue placeholder="Seleccionar incoterm" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="EXW">EXW - Ex Works</SelectItem>
                            <SelectItem value="FCA">FCA - Free Carrier</SelectItem>
                            <SelectItem value="FOB">FOB - Free on Board</SelectItem>
                            <SelectItem value="CIF">CIF - Cost Insurance Freight</SelectItem>
                            <SelectItem value="DDP">DDP - Delivered Duty Paid</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="c4cOpportunityId">OPP C4C ID</Label>
                        <Input
                          id="c4cOpportunityId"
                          placeholder="ID Oportunidad SAP C4C"
                          value={formData.c4cOpportunityId}
                          onChange={(e) =>
                            setFormData({ ...formData, c4cOpportunityId: e.target.value })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="c4cTaskId">TAREA C4C ID</Label>
                        <Input
                          id="c4cTaskId"
                          placeholder="ID Tarea SAP C4C"
                          value={formData.c4cTaskId}
                          onChange={(e) => setFormData({ ...formData, c4cTaskId: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="notes">Notas</Label>
                        <Textarea
                          id="notes"
                          rows={1}
                          value={formData.notes}
                          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Sections - stacked vertically */}
                <div data-tour="create-sections">
                  {sections.map((section, index) => {
                    const isImportQuote =
                      !!selectedQuoteType &&
                      ['CO', 'IMPORT', 'IMP', 'IMP-SW'].includes(selectedQuoteType.code);
                    return (
                      <QuoteSectionCard
                        key={section.id}
                        sectionIndex={index}
                        models={models}
                        materials={materials}
                        section={section}
                        allSections={sections}
                        onUpdate={(updated) => handleUpdateSection(index, updated)}
                        onRemove={() => handleRemoveSection(index)}
                        currencySymbol={selectedCurrency?.symbol}
                        canRemove={sections.length > 1}
                        upstreamPrices={
                          selectedQuoteType?.upstreamTypeId ? upstreamPrices : undefined
                        }
                        upstreamVariableCode={selectedQuoteType?.upstreamVariableCode}
                        upstreamTotalVariableCode={selectedQuoteType?.upstreamTotalVariableCode}
                        freightByLineItemId={freightByLineItemId}
                        shipments={isImportQuote ? shipments : undefined}
                        onShipmentChange={isImportQuote ? handleShipmentChange : undefined}
                        sourceQuoteSections={sourceQuoteSections}
                        sourceQuoteAllItems={sourceQuoteAllItems}
                        sourceQuoteCode={sourceQuoteCode}
                      />
                    );
                  })}
                </div>

                {/* Add Section Button */}
                <div className="flex justify-center">
                  <Button
                    type="button"
                    variant="outline"
                    size="lg"
                    onClick={handleAddSection}
                    className="border-dashed border-2"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Agregar Sección (Pricer)
                  </Button>
                </div>

                {/* Freight Section for COMPRA type quotes */}
                {selectedQuoteType &&
                  ['CO', 'IMPORT', 'IMP', 'IMP-SW'].includes(selectedQuoteType.code) && (
                    <QuoteLogisticsSection
                      key={logisticsRefreshKey}
                      quoteId={id}
                      incoterm={quoteIncoterm}
                      incotermConfig={incotermConfig}
                      sections={sections as any}
                      materials={materials as any}
                      onRequireSave={ensureSaved}
                      onFreightCalculated={(totalFreight, totalInsurance) =>
                        setFreightTotals({ totalFreight, totalInsurance })
                      }
                      onShipmentsChanged={(nextShipments) => {
                        setShipments(nextShipments);
                        // Sincronizar shipmentId en los lineItems de cada sección (para líneas persistidas)
                        setSections((prev) =>
                          prev.map((section) => ({
                            ...section,
                            lineItems: section.lineItems.map((li) => {
                              if (!li.id) return li;
                              const shipOwning = nextShipments.find((sh) =>
                                (sh.lineItems ?? []).some((shLi) => shLi.id === li.id)
                              );
                              return shipOwning ? { ...li, shipmentId: shipOwning.id } : li;
                            }),
                          }))
                        );
                      }}
                      onLegsChanged={!id ? setLocalFreightLegs : undefined}
                      onLineShipmentChanged={(lineItemId, shipmentId) => {
                        setSections((prev) =>
                          prev.map((s) => ({
                            ...s,
                            lineItems: s.lineItems.map((li) =>
                              li.id === lineItemId ? { ...li, shipmentId } : li
                            ),
                          }))
                        );
                      }}
                    />
                  )}

                {/* Grand Total Banner */}
                {grandTotal > 0 && (
                  <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-alerta-borde">
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <span className="text-lg font-semibold text-foreground">
                          GRAN TOTAL ({sections.length}{' '}
                          {sections.length === 1 ? 'sección' : 'secciones'})
                        </span>
                        <span className="text-2xl font-bold text-foreground">
                          {selectedCurrency?.symbol || '$'}{' '}
                          {formatNumber(
                            grandTotal + freightTotals.totalFreight + freightTotals.totalInsurance
                          )}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Error del último intento de submit — visible, no se va hasta que reintente */}
              {submitError && (
                <div className="mt-6 rounded-lg border border-bloqueo-borde bg-bloqueo-suave p-3 text-sm text-foreground flex items-start justify-between gap-3">
                  <div>
                    <span className="font-semibold">Error al guardar:</span> {submitError}
                  </div>
                  <button
                    type="button"
                    className="text-bloqueo hover:text-foreground text-xs underline"
                    onClick={() => setSubmitError(null)}
                  >
                    cerrar
                  </button>
                </div>
              )}
              {/* Actions */}
              {(() => {
                const missing: string[] = [];
                if (!formData.clientName.trim()) missing.push('nombre del cliente');
                if (
                  !formData.clientEmail.trim() ||
                  !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.clientEmail)
                )
                  missing.push('email del cliente');
                if (!formData.currencyId) missing.push('divisa');
                const allLineItems = sections.flatMap((s) => s.lineItems);
                if (allLineItems.length === 0) missing.push('al menos un ítem');
                else if (allLineItems.some((li) => !li.materialId))
                  missing.push('material en todos los ítems');
                if (sections.some((s) => !s.modelId)) missing.push('modelo en todas las secciones');
                return missing.length > 0 ? (
                  <div className="mt-6 rounded-lg border border-alerta-borde bg-alerta-suave p-3 text-sm text-foreground">
                    <span className="font-semibold">Faltan campos para finalizar:</span>{' '}
                    {missing.join(', ')}.
                  </div>
                ) : null;
              })()}
              <div data-tour="create-submit" className="flex justify-end gap-4 mt-6">
                {hasDraftData && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={async () => {
                      if (
                        await confirmar({
                          title: '¿Empezar de cero?',
                          description: 'Se descartan todos los datos del formulario actual.',
                          variant: 'destructive',
                          confirmText: 'Sí, empezar de cero',
                        })
                      ) {
                        resetDraft();
                      }
                    }}
                  >
                    Empezar de cero
                  </Button>
                )}
                <Button type="button" variant="outline" onClick={() => navigate('/quotes')}>
                  Cancelar
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={isSavingDraft || isLoading || isSubmitting}
                  onClick={() => handleSaveDraft()}
                >
                  {isSavingDraft ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Guardar Borrador
                    </>
                  )}
                </Button>
                <Button type="submit" disabled={isLoading || isSavingDraft || isSubmitting}>
                  {isLoading || isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {isEditMode ? 'Guardando...' : autoSavedId ? 'Finalizando...' : 'Creando...'}
                    </>
                  ) : isEditMode ? (
                    'Guardar Cambios'
                  ) : autoSavedId ? (
                    'Finalizar Cotización'
                  ) : (
                    'Crear Cotización'
                  )}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>

      <QuickCreateCustomerDialog
        open={showQuickCreateCustomer}
        onOpenChange={setShowQuickCreateCustomer}
        onCreated={(customer) => {
          setFormData({
            ...formData,
            clientName: customer.name,
            clientEmail: customer.email || '',
            clientPhone: customer.phone || '',
          });
          setFormErrors((prev) => {
            const n = { ...prev };
            delete n.clientName;
            return n;
          });
        }}
      />
    </AppLayout>
  );
}

export default QuoteCreatePage;
